import { defineStore } from 'pinia'

import type { ResponseWrapper } from '@/types/external/2work/api/response/ResponseWrapper'

import { useFrappeCallAPIStore } from '@/stores/apis/frappeCallAPI'

const controllerNamePrefix = 'erp_2work.erp_2work.controller'

interface SsoProvider {
  /** Absolute authorize URL of the identity provider, ready to navigate to. */
  auth_url: string
  /** Display name shown on the login button, e.g. "cloud.desk". */
  label: string
  /** Name of the Frappe "Social Login Key" document. */
  name: string
}

interface SsoLogoutUrl {
  logout_url: null | string
}

type GetProvidersFunction = (redirectTo?: string) => Promise<SsoProvider[]>
type GetLogoutUrlFunction = () => Promise<null | string>

interface FrappeSsoAPIStore {
  getLogoutUrl: GetLogoutUrlFunction
  getProviders: GetProvidersFunction
}

/**
 * ! Caution:
 * * all "frappeInstance" function calls must be wrapped by
 * * "transformFrappeErrorObject" function, for consistent error handling
 */

const useFrappeSsoAPIStore = defineStore('frappeSsoAPI', (): FrappeSsoAPIStore => {
  const callAPIStore = useFrappeCallAPIStore()

  const getProviders: GetProvidersFunction = async (redirectTo) => {
    const response = await callAPIStore.get<ResponseWrapper<SsoProvider[]>>(
      `${controllerNamePrefix}.sso.get_providers`,
      redirectTo ? { redirect_to: redirectTo } : {},
    )
    return response.data
  }

  const getLogoutUrl: GetLogoutUrlFunction = async () => {
    const response = await callAPIStore.get<ResponseWrapper<SsoLogoutUrl>>(
      `${controllerNamePrefix}.sso.get_logout_url`,
      {},
    )
    return response.data.logout_url
  }

  return {
    getLogoutUrl,
    getProviders,
  }
})

export { type SsoProvider, useFrappeSsoAPIStore }
